import React, { useEffect, useState } from 'react';
import { Play, Square, RotateCw, Trash2, FileText, Activity, RefreshCw, Box, Terminal } from 'lucide-react';
import DockerService from '../../../services/DockerService';
import ServiceManagementModal from './ServiceManagementModal';
import { toast } from 'react-toastify';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../../../components/ui/Table";
import { Badge } from "../../../components/ui/Badge";
import { IconButton } from "../../../components/ui/Button";

const DockerContainers = ({ serverId }) => {
    const [containers, setContainers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(null); // stores container name being processed
    const [selectedContainer, setSelectedContainer] = useState(null); // for management modal

    // Helper to format status color
    const getStatusColor = (status) => {
        if (status.includes('Up')) return 'text-green-400 bg-green-400/10 border-green-400/20';
        if (status.includes('Exited')) return 'text-red-400 bg-red-400/10 border-red-400/20';
        return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20';
    };

    const fetchContainers = async () => {
        setLoading(true);
        try {
            const resp = await DockerService.listContainers(serverId);
            if (resp.state) {
                setContainers(resp.data.containers);
            } else {
                toast.error(resp.message);
            }
        } catch (error) {
            console.error(error);
            toast.error("Container listesi alınamadı");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchContainers();
    }, [serverId]);

    const handleAction = async (action, containerName) => {
        setProcessing(containerName);
        try {
            let resp;
            switch (action) {
                case 'start':
                    resp = await DockerService.startContainer(serverId, containerName);
                    break;
                case 'stop':
                    resp = await DockerService.stopContainer(serverId, containerName);
                    break;
                case 'restart':
                    resp = await DockerService.restartContainer(serverId, containerName);
                    break;
                case 'remove':
                    if (window.confirm(`${containerName} silinecek. Emin misiniz?`)) {
                        resp = await DockerService.removeContainer(serverId, containerName);
                    } else {
                        setProcessing(null);
                        return;
                    }
                    break;
                default:
                    return;
            }

            if (resp.state) {
                toast.success(resp.message);
                fetchContainers(); // Refresh list
            } else {
                toast.error(resp.message || resp.data?.error || "İşlem başarısız");
            }
        } catch (error) {
            console.error(error);
            toast.error("İşlem sırasında hata oluştu");
        } finally {
            setProcessing(null);
        }
    };

    const showContainerModal = (containerName) => {
        const container = containers.find(c => c.name === containerName);
        if (container) {
            setSelectedContainer(container);
        }
    };

    return (
        <div className="space-y-6 animate-fadeIn pb-6">
            {/* Toolbar */}
            <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Box className="w-5 h-5 text-blue-400" />
                    <h2 className="text-lg font-semibold text-white">Docker Konteynerleri</h2>
                </div>
                <button
                    onClick={fetchContainers}
                    disabled={loading}
                    className="p-2 text-gray-400 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                    <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Container List */}
            <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Konteyner</TableHead>
                            <TableHead>Görsel</TableHead>
                            <TableHead>Durum</TableHead>
                            <TableHead>Portlar</TableHead>
                            <TableHead className="text-right">İşlemler</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && containers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                                    <div className="flex flex-col items-center justify-center gap-2">
                                        <RefreshCw className="w-6 h-6 animate-spin text-blue-500/50" />
                                        <span>Yükleniyor...</span>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ) : containers.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-gray-500 py-8">
                                    Container bulunamadı.
                                </TableCell>
                            </TableRow>
                        ) : (
                            containers.map((container, idx) => (
                                <TableRow key={container.id || idx} className="group">
                                    <TableCell>
                                        <div className="flex flex-col">
                                            <span className="font-medium text-white">{container.name}</span>
                                            <span className="text-xs text-gray-500 font-mono mt-0.5">{container.id.substring(0, 12)}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-300 font-mono">{container.image}</TableCell>
                                    <TableCell>
                                        <Badge
                                            variant={container.status.includes('Up') ? 'success' : 'secondary'}
                                            className={getStatusColor(container.status).replace('border', '')} // Removing border as badge has one, or keep standard variants
                                        >
                                            {container.status}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-sm text-gray-400 max-w-xs truncate" title={container.ports}>
                                        {container.ports || '-'}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex justify-end items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                                            <IconButton
                                                onClick={() => handleAction('start', container.name)}
                                                disabled={processing === container.name || container.status.includes('Up')}
                                                icon={Play}
                                                variant="success"
                                                title="Başlat"
                                            />
                                            <IconButton
                                                onClick={() => handleAction('stop', container.name)}
                                                disabled={processing === container.name || !container.status.includes('Up')}
                                                icon={Square}
                                                variant="destructive"
                                                title="Durdur"
                                            />
                                            <IconButton
                                                onClick={() => handleAction('restart', container.name)}
                                                disabled={processing === container.name}
                                                icon={RotateCw}
                                                variant="info"
                                                className={processing === container.name ? 'animate-spin' : ''}
                                                title="Yeniden Başlat"
                                            />
                                            <div className="w-px h-4 bg-white/10 mx-1"></div>
                                            <IconButton
                                                onClick={() => showContainerModal(container.name)}
                                                icon={FileText}
                                                variant="ghost"
                                                title="Yönet"
                                            />
                                            <IconButton
                                                onClick={() => handleAction('remove', container.name)}
                                                disabled={processing === container.name || container.status.includes('Up')}
                                                icon={Trash2}
                                                variant="ghost"
                                                className="hover:text-red-400 hover:bg-red-400/10"
                                                title="Kaldır"
                                            />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Service Management Modal */}
            <ServiceManagementModal
                isOpen={!!selectedContainer}
                onClose={() => {
                    setSelectedContainer(null);
                    fetchContainers(); // Refresh list after modal closes
                }}
                container={selectedContainer}
                serverId={serverId}
            />
        </div>
    );
};

export default DockerContainers;
